import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import {
  type EnrollmentHYCSSecureAccountData,
  type EnrollmentHYCSSecureAccountSubmitInput,
  type EnrollmentHYCSSecureAccountSubmitOutput,
} from '../../../../models'

/**
 * Get Enrollment HYCS SecureAccount data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSSecureAccountData>
 * @example const response = await getEnrollmentHYCSSecureAccountData()
 */
export const getEnrollmentHYCSSecureAccountData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSSecureAccountData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSSecureAccountData',
      query: `mutation getEnrollmentHYCSSecureAccountData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              passkeyRegistered
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSSecureAccountData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSSecureAccountData = createQueryHook<
  EnrollmentHYCSSecureAccountSubmitInput,
  EnrollmentHYCSSecureAccountData
>(getEnrollmentHYCSSecureAccountData)

/**
 * Get Enrollment HYCS SecureAccount data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSSecureAccountData>
 * @example const response = await getEnrollmentHYCSSecureAccountData()
 */
export const getEnrollmentPasskeyCredentialOptions = async (
  enrollmentSecureAccountSubmitInput: EnrollmentHYCSSecureAccountSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSSecureAccountData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentPasskeyCredentialOptions',
      query: `mutation getEnrollmentPasskeyCredentialOptions($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              passkeyPublicKeyCredentialRequestOptions
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentSecureAccountSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSSecureAccountData }
  return response.submitEnrollment
}

export const useGetEnrollmentPasskeyCredentialOptions = createMutationHook<
  EnrollmentHYCSSecureAccountSubmitInput,
  EnrollmentHYCSSecureAccountData
>(getEnrollmentPasskeyCredentialOptions)

/**
 * Submit Enrollment HYCS SecureAccount data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSSecureAccountSubmitOutput>
 * @example const response = await submitEnrollmentHYCSSecureAccountData();
 */
export const submitEnrollmentHYCSSecureAccountData = async (
  enrollmentSecureAccountSubmitInput: EnrollmentHYCSSecureAccountSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSSecureAccountSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSSecureAccountData',
      query: `mutation submitEnrollmentHYCSSecureAccountData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            complete
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentSecureAccountSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSSecureAccountSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSSecureAccountData = createMutationHook<
  EnrollmentHYCSSecureAccountSubmitInput,
  EnrollmentHYCSSecureAccountSubmitOutput
>(submitEnrollmentHYCSSecureAccountData)
